import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Search, Loader2, CheckCircle, XCircle, AlertTriangle } from "lucide-react";

/**
 * NIP Lookup Button - fetches company data from GUS/VIES by NIP
 * @param {string} nip - NIP value to lookup
 * @param {function} onResult - callback with fetched data {name, nip, regon, address, status, vatStatus}
 * @param {string} className - additional CSS classes
 */
export function NipLookupButton({ nip, onResult, className = "", disabled = false }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const [errorMsg, setErrorMsg] = useState("");

  const lookup = async () => {
    // Clean NIP - remove all non-digits
    const cleanedNip = nip ? nip.replace(/\D/g, '') : '';
    
    if (!cleanedNip || cleanedNip.length < 10) {
      setStatus('error');
      setErrorMsg('NIP musi mieć minimum 10 cyfr');
      setTimeout(() => { setStatus(null); setErrorMsg(""); }, 3000);
      return;
    }

    setLoading(true);
    setStatus(null);
    setErrorMsg("");

    try {
      const res = await api.get(`/nip/lookup/${cleanedNip}`);
      if (res.data && res.data.name) {
        onResult(res.data);
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMsg('Nie znaleziono danych');
      }
    } catch (err) {
      console.error("NIP lookup error:", err);
      setStatus('error');
      setErrorMsg(err?.response?.data?.detail || 'Błąd pobierania danych');
    } finally {
      setLoading(false);
      setTimeout(() => { setStatus(null); setErrorMsg(""); }, 3000);
    }
  };

  return (
    <div className="flex flex-col">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={`h-9 rounded-lg text-xs gap-1 ${status === 'error' ? 'border-red-300' : status === 'success' ? 'border-green-300' : ''} ${className}`}
        onClick={lookup}
        disabled={loading || disabled || !nip}
        title="Pobierz dane z CEIDG/VIES"
      >
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : status === 'success' ? (
          <CheckCircle className="h-3.5 w-3.5 text-green-600" />
        ) : status === 'error' ? (
          <XCircle className="h-3.5 w-3.5 text-red-500" />
        ) : (
          <Search className="h-3.5 w-3.5" />
        )}
        <span className="hidden sm:inline">
          {loading ? "Szukam..." : status === 'success' ? "Znaleziono!" : status === 'error' ? "Błąd" : "Pobierz dane"}
        </span>
      </Button>
      {errorMsg && <span className="text-[10px] text-red-500 mt-1">{errorMsg}</span>}
    </div>
  );
}

/**
 * VAT Status Badge - shows VAT status with appropriate color
 */
export function VatStatusBadge({ vatStatus, className = "" }) {
  if (!vatStatus) return null;

  const isActive = vatStatus === "Czynny" || vatStatus === "ACTIVE";
  const isInactive = vatStatus === "Nieczynny" || vatStatus === "INACTIVE" || vatStatus === "Zwolniony";

  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${className} ${
      isActive 
        ? "bg-green-100 text-green-700 border border-green-200" 
        : isInactive 
          ? "bg-red-100 text-red-700 border border-red-200"
          : "bg-slate-100 text-slate-600 border border-slate-200"
    }`}>
      {isActive ? (
        <CheckCircle className="h-3 w-3" />
      ) : isInactive ? (
        <AlertTriangle className="h-3 w-3" />
      ) : null}
      <span>VAT: {vatStatus}</span>
    </div>
  );
}
